import {Injectable} from '@angular/core';
import localforage from 'localforage';

@Injectable({ providedIn: 'root' })
export class LocalForageUtilities {
  constructor() {
    localforage.config({
      name: 'manual-assist-ai',
      storeName: 'manual-cache'
    });
  }

  async set<T>(key: string, value: T, ttlMs: number): Promise<void> {
    const expiry = Date.now() + ttlMs;
    await localforage.setItem(key, { value, expiry });
  }

  async get<T>(key: string): Promise<T | null> {
    const item = await localforage.getItem<{ value: T, expiry: number }>(key);
    if (!item) return null;

    if (Date.now() > item.expiry) {
      await localforage.removeItem(key);
      return null;
    }
    return item.value;
  }

  async remove(key: string): Promise<void> {
    await localforage.removeItem(key);
  }

  async clear(): Promise<void> {
    await localforage.clear();
  }

  async cleanupExpired(): Promise<void> {
    try {
      // Wait for localForage to be ready
      const isAvailable = await localforage.ready().then(() => true).catch(() => false);

      if (!isAvailable) {
        console.log('[LocalForageUtilities] Storage not available');
        return;
      }

      let removedCount = 0;
      const now = Date.now();

      await localforage.iterate((value, key) => {
        try {
          const item = value as { value: unknown; expiry: number };

          // Defensive check in case item wasn't saved with expiry
          if (!item?.expiry || typeof item.expiry !== 'number') return;

          if (now > item.expiry) {
            // Don't await here - we'll handle removal asynchronously
            // to avoid slowing down the iteration
            localforage.removeItem(key);
            removedCount++;
          }
        } catch (err) {
          console.error(`[LocalForageUtilities] Error processing key ${key}:`, err);
        }
      });
    } catch (err) {
      console.error('[LocalForageUtilities] Error during cleanup:', err);
    }
  }

}
